import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  addProjectMember,
  createTask,
  createView,
  deleteProject,
  deleteTask,
  deleteView,
  getBacklog,
  getMe,
  getProject,
  getProjectCalendar,
  getProjectTable,
  getProjectTimeline,
  getWorkload,
  listAssignees,
  listProjectMembers,
  listProjectTasks,
  listTerms,
  removeProjectMember,
  requestProjectAccess,
  rolloverProject,
  setProjectVisibility,
  updateProject,
  updateProjectMember,
  updateTask,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useTaskPriorities as usePriorities, taskPriorityCode } from "../lib/priorities";
import { formatEstimate } from "../lib/format";
import { getCurrentUser } from "../lib/auth";
import type {
  AcademicTermRow,
  Assignee,
  BoardSummary,
  ProjectMember,
  ProjectRole,
  ProjectRow,
  ProjectVisibility,
  SavedViewRow,
  TaskRow,
  UpdateProjectInput,
  ViewType,
} from "../lib/types";
import { PageBar } from "../components/Layout";
import { PriorityBadge, StatusBadge } from "../components/Badge";
import { Avatar, AvatarStack } from "../components/Avatar";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { TagsEditor } from "../components/Tags";
import { BoardCanvas } from "./BoardPage";
import { TaskDrawer } from "../components/TaskDrawer";
import { TASK_STATUSES } from "../lib/constants";

const ROLE_LABEL: Record<ProjectRole, string> = {
  viewer: "Viewer",
  editor: "Editor",
  manager: "Manager",
};

/** The 7 view types + an icon/label, used by the switcher and the "add view" picker. */
const VIEW_META: Record<ViewType, { icon: string; label: string }> = {
  list: { icon: "≣", label: "List" },
  board: { icon: "🗂", label: "Board" },
  table: { icon: "▦", label: "Table" },
  timeline: { icon: "📊", label: "Timeline" },
  calendar: { icon: "📅", label: "Calendar" },
  workload: { icon: "⚖", label: "Workload" },
  chart: { icon: "📈", label: "Chart" },
};
const VIEW_TYPES = Object.keys(VIEW_META) as ViewType[];

/** A non-view extra tab (not backed by a saved_view row). */
type ExtraTab = "backlog" | "members";

function fmtDay(unixSec?: number | null): string {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtFullDay(unixSec?: number | null): string {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function priorityClass(p?: string | null): string {
  switch ((p ?? "").toLowerCase()) {
    case "urgent":
      return "pr-urgent";
    case "high":
      return "pr-high";
    case "medium":
    case "normal":
      return "pr-normal";
    case "low":
      return "pr-low";
    default:
      return "pr-low";
  }
}

/** Map a status code to a semantic colour token (same buckets the StatusBadge uses)
 *  so view bars/chips re-skin in dark via the tokens. */
function statusToken(code?: string | null): string {
  switch ((code ?? "").toLowerCase()) {
    case "open":
    case "todo":
    case "backlog":
      return "var(--st-open)";
    case "in-progress":
    case "in_progress":
    case "pending":
    case "doing":
      return "var(--st-progress)";
    case "solved":
    case "done":
    case "complete":
    case "completed":
      return "var(--st-done)";
    case "blocked":
      return "var(--st-blocked)";
    case "closed":
    case "cancelled":
    case "canceled":
      return "var(--st-closed)";
    default:
      return "var(--st-open)";
  }
}

/** Map a priority code to a semantic colour token. */
function priorityToken(code?: string | null): string {
  switch ((code ?? "").toLowerCase()) {
    case "urgent":
      return "var(--st-blocked)";
    case "high":
      return "var(--st-progress)";
    case "normal":
    case "medium":
      return "var(--st-open)";
    case "low":
      return "var(--st-closed)";
    default:
      return "var(--st-closed)";
  }
}

const DAY_SEC = 86_400;

/** Start-of-local-day (unix seconds) for a unix-second timestamp. */
function dayStartSec(unixSec: number): number {
  const d = new Date(unixSec * 1000);
  return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 1000);
}

/** Project header/view customization (persisted in projects.ui_config JSON). */
export interface ProjectUiConfig {
  compact: boolean;
  note: string;
  showDesc: boolean;
  showMeta: boolean;
}
export function parseProjectUi(raw?: string | null): ProjectUiConfig {
  let p: Partial<ProjectUiConfig> = {};
  try {
    if (raw) p = JSON.parse(raw) as Partial<ProjectUiConfig>;
  } catch {
    /* malformed config → defaults */
  }
  return {
    compact: !!p.compact,
    note: typeof p.note === "string" ? p.note : "",
    showDesc: p.showDesc !== false,
    showMeta: p.showMeta !== false,
  };
}

/** Gradient hero header + glassy meta row built from the project's real data. */
function ProjectHero({
  project,
  ui,
  memberCount,
  memberNames,
  onManageMembers,
  onCustomize,
}: {
  project: ProjectRow;
  ui: ProjectUiConfig;
  memberCount: number;
  memberNames: string[];
  onManageMembers: () => void;
  onCustomize: () => void;
}) {
  const visibility = project.visibility ?? "shared";
  // Team now lives in the header (members + invite), so drop it from the meta row.
  const hasMeta =
    ui.showMeta && (!!project.priority || !!project.start_date || !!project.end_date);
  return (
    <>
      <section
        className={`project-hero${project.color ? " themed" : ""}`}
        style={project.color ? { ["--hero-accent" as string]: project.color } : undefined}
      >
        <button
          type="button"
          className="hero-customize"
          onClick={onCustomize}
          title="Customize this project's header"
          aria-label="Customize header"
        >
          ⚙ Customize
        </button>
        <div className="hero-crumbs">
          <Link to="/projects">Projects</Link> <span aria-hidden="true">/</span> {project.name}
        </div>
        <div className="hero-row">
          <div className="hero-id">
            {project.icon ? (
              <span className="hero-icon" aria-hidden="true">
                {project.icon}
              </span>
            ) : null}
            <div>
              <h1>
                {project.name}
                <span className="hero-key mono">{project.key}</span>
              </h1>
              {ui.showDesc && project.description ? <p className="hero-desc">{project.description}</p> : null}
              {ui.note ? <p className="hero-note">{ui.note}</p> : null}
            </div>
          </div>
          <div className="hero-aside">
            {project.lead_name ? (
              <div className="hero-owner">
                <span className="hero-owner-k">Owner</span>
                <span className="hero-owner-v">
                  <Avatar name={project.lead_name} />
                  {project.lead_name}
                </span>
              </div>
            ) : null}
            <div className="hero-members">
              {memberCount > 0 ? (
                <button
                  type="button"
                  className="hero-members-stack"
                  onClick={onManageMembers}
                  aria-label={`${memberCount} member${memberCount === 1 ? "" : "s"} — manage`}
                  title="Manage members"
                >
                  <AvatarStack names={memberNames} max={4} />
                  <span className="hero-members-count">
                    {memberCount} member{memberCount === 1 ? "" : "s"}
                  </span>
                </button>
              ) : null}
              <button type="button" className="hero-invite" onClick={onManageMembers}>
                <span aria-hidden="true">+</span> Invite member
              </button>
            </div>
          </div>
        </div>
      </section>

      {hasMeta ? (
        <div className="project-meta">
          {project.priority ? (
            <div className="m">
              <span className="k">Priority</span>
              <span className="v">
                <span className={`badge ${priorityClass(project.priority)}`}>{project.priority}</span>
              </span>
            </div>
          ) : null}
          {project.start_date ? (
            <div className="m">
              <span className="k">Start</span>
              <span className="v">{fmtFullDay(project.start_date)}</span>
            </div>
          ) : null}
          {project.end_date ? (
            <div className="m">
              <span className="k">Due</span>
              <span className="v">{fmtFullDay(project.end_date)}</span>
            </div>
          ) : null}
          <div className="m">
            <span className="k">Visibility</span>
            <span className="v">
              <span className={`meta-chip vis-${visibility}`}>
                <span className="ic-dot" aria-hidden="true" />
                {visibility === "private" ? "Private" : "Shared"}
              </span>
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function ProjectDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddView, setShowAddView] = useState(false);
  const [showRollover, setShowRollover] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // Project-level header/density customization (persisted on the project).
  const [showCustomize, setShowCustomize] = useState(false);
  // Bumped after a task is created to remount/refetch the active view.
  const [viewNonce, setViewNonce] = useState(0);
  const deleteProjMut = useMutation(deleteProject);
  const deleteViewMut = useMutation(deleteView);

  const projectApi = useApi((signal) => getProject(id, signal), [id]);
  const project = projectApi.data?.project;
  const views = useMemo<SavedViewRow[]>(() => projectApi.data?.views ?? [], [projectApi.data]);
  const boards = useMemo<BoardSummary[]>(() => projectApi.data?.boards ?? [], [projectApi.data]);

  // Members power the hero avatar stack. Loaded best-effort: a 403/empty just
  // hides the stack — it never blocks the page.
  const membersApi = useApi((signal) => listProjectMembers(id, signal), [id]);
  const memberNames = useMemo<string[]>(
    () => (membersApi.data?.members ?? []).map((m) => m.name).filter(Boolean),
    [membersApi.data],
  );

  // The active tab key: a saved view id, or "extra:backlog" / "extra:members".
  // Default to the first saved view (the seed orders List, then Board) so a
  // project always opens on a real view.
  const activeKey = active ?? (views[0] ? views[0].id : "extra:backlog");
  const activeView = views.find((v) => v.id === activeKey);
  const activeExtra: ExtraTab | null = activeKey.startsWith("extra:")
    ? (activeKey.slice("extra:".length) as ExtraTab)
    : null;

  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/projects">Projects</Link> <span aria-hidden="true">/</span>{" "}
            {project?.name ?? "Project"}
          </>
        }
        actions={
          <>
            <button className="btn primary" onClick={() => setShowCreate(true)} disabled={!project}>
              + New project task
            </button>
            <button
              className="btn"
              disabled={!project}
              onClick={() => setShowRollover(true)}
              title="Clone this project into a new academic term"
            >
              Roll over…
            </button>
            <button
              className="btn"
              disabled={!project}
              onClick={() => setShowEdit(true)}
              title="Edit this project's details"
            >
              Edit
            </button>
            <button
              className="btn danger"
              disabled={!project || deleteProjMut.pending}
              onClick={() =>
                project &&
                void confirmDialog({
                  title: "Delete project",
                  message: `Delete project "${project.name}" and all its boards, tasks, and views? This can't be undone.`,
                  danger: true,
                }).then((yes) => {
                  if (yes) void deleteProjMut.run(project.id).then((ok) => ok && navigate("/projects"));
                })
              }
            >
              {deleteProjMut.pending ? <Spinner label="Deleting…" /> : "Delete project"}
            </button>
          </>
        }
      />
      <div className={`content${project && parseProjectUi(project.ui_config).compact ? " project-compact" : ""}`}>
        {projectApi.loading ? (
          <Loading label="Loading project…" />
        ) : projectApi.error ? (
          isForbiddenError(projectApi.error) ? (
            <RequestAccessPanel projectId={id} />
          ) : (
            <ErrorState error={projectApi.error} onRetry={projectApi.reload} />
          )
        ) : !project ? (
          <EmptyState>Project not found.</EmptyState>
        ) : (
          <>
            <ProjectHero
              project={project}
              ui={parseProjectUi(project.ui_config)}
              memberCount={memberNames.length}
              memberNames={memberNames}
              onManageMembers={() => setActive("extra:members")}
              onCustomize={() => setShowCustomize(true)}
            />

            {/* View switcher: one tab per saved view (List / Board / Timeline /
                Calendar / Table / Workload / Chart), then Backlog + Members, then
                an "+ Add view" action to create a new view of any type. */}
            <div className="tabs" role="tablist">
              {views.map((v) => {
                const meta = VIEW_META[(v.view_type as ViewType)] ?? { icon: "≣", label: v.view_type };
                return (
                  <span key={v.id} className={`tab tab-with-action${activeKey === v.id ? " active" : ""}`}>
                    <button
                      role="tab"
                      aria-selected={activeKey === v.id}
                      className="tab-label"
                      onClick={() => setActive(v.id)}
                      title={`${meta.label} view`}
                    >
                      <span aria-hidden="true">{meta.icon}</span> {v.name}
                    </button>
                    <button
                      className="tab-x"
                      title="Delete view"
                      aria-label={`Delete ${v.name} view`}
                      disabled={deleteViewMut.pending}
                      onClick={() =>
                        void confirmDialog({ message: `Delete the "${v.name}" view?`, danger: true }).then((yes) => {
                          if (!yes) return;
                          void deleteViewMut.run(v.id).then((ok) => {
                            if (ok) {
                              if (activeKey === v.id) setActive(null);
                              projectApi.reload();
                            }
                          });
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
              {/* Divider: data VIEWS on the left, project-management tabs on the right. */}
              <span className="tab-sep" aria-hidden="true" />
              {(["backlog", "members"] as ExtraTab[]).map((t) => {
                const key = `extra:${t}`;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={activeKey === key}
                    className={`tab${activeKey === key ? " active" : ""}`}
                    onClick={() => setActive(key)}
                  >
                    {t === "backlog" ? "Backlog" : "Members & access"}
                  </button>
                );
              })}
              <button
                className="tab"
                onClick={() => setShowAddView(true)}
                title="Add a view"
                style={{ marginLeft: "auto" }}
              >
                + Add view
              </button>
            </div>

            {activeView ? (
              // Keyed by view + nonce: bumping the nonce remounts the renderer so
              // a just-created task appears even when the target view is already open.
              <ViewRenderer key={`${activeView.id}:${viewNonce}`} projectId={id} view={activeView} boards={boards} />
            ) : activeExtra === "backlog" ? (
              <BacklogTab projectId={id} />
            ) : activeExtra === "members" ? (
              <MembersTab projectId={id} visibility={project.visibility ?? "shared"} />
            ) : null}
          </>
        )}
      </div>

      {showCreate && project ? (
        <CreateTaskModal
          projectId={project.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            // Jump to a list/table view (or backlog) so the new task is visible,
            // and bump the nonce so an already-open view refetches.
            const listView = views.find((v) => v.view_type === "list" || v.view_type === "table");
            setActive(listView ? listView.id : "extra:backlog");
            setViewNonce((n) => n + 1);
          }}
        />
      ) : null}

      {showAddView && project ? (
        <CreateViewModal
          projectId={project.id}
          onClose={() => setShowAddView(false)}
          onCreated={(view) => {
            setShowAddView(false);
            projectApi.reload();
            setActive(view.id);
          }}
        />
      ) : null}

      {showRollover && project ? (
        <RolloverModal
          project={project}
          onClose={() => setShowRollover(false)}
          onDone={(newId) => {
            setShowRollover(false);
            navigate(`/projects/${newId}`);
          }}
        />
      ) : null}

      {showEdit && project ? (
        <EditProjectModal
          project={project}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            // Reload the project so the hero (name, badges, dates, lead, colour) reflects the edit.
            projectApi.reload();
          }}
        />
      ) : null}

      {showCustomize && project ? (
        <CustomizeProjectModal
          project={project}
          onClose={() => setShowCustomize(false)}
          onSaved={() => {
            setShowCustomize(false);
            projectApi.reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Project header/view customization — density, accent colour, a header note
 *  ("text section"), and which header sections show. Persisted on the project
 *  (projects.ui_config + projects.color), so it applies for every member. */
function CustomizeProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = parseProjectUi(project.ui_config);
  const [compact, setCompact] = useState(initial.compact);
  const [note, setNote] = useState(initial.note);
  const [showDesc, setShowDesc] = useState(initial.showDesc);
  const [showMeta, setShowMeta] = useState(initial.showMeta);
  const [accent, setAccent] = useState<string | null>(project.color ?? null);
  const { run, pending, error } = useMutation((patch: UpdateProjectInput) => updateProject(project.id, patch));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const ui_config = JSON.stringify({ compact, note: note.trim(), showDesc, showMeta });
    const res = await run({
      ui_config,
      ...(accent !== (project.color ?? null) ? { color: accent } : {}),
    });
    if (res) onSaved();
  }

  return (
    <Modal
      title="Customize project header"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="customize-project-form" disabled={pending}>
            {pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="customize-project-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Accent color</span>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={accent != null}
              onChange={(e) => setAccent(e.target.checked ? accent ?? "#6366f1" : null)}
              aria-label="Use a custom accent color"
            />
            <input
              type="color"
              value={accent ?? "#6366f1"}
              disabled={accent == null}
              onChange={(e) => setAccent(e.target.value)}
              style={{ width: 44, height: 30, padding: 0, border: "none", background: "none", cursor: accent == null ? "not-allowed" : "pointer" }}
            />
            <span className="faint">{accent ?? "Default gradient"}</span>
          </div>
          <span className="hint">Tints the project header. Shared with all members.</span>
        </label>
        <label className="field">
          <span className="label">Header note</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="A short note shown under the title (e.g. “Q3 priority — migrating from Slate”)"
            style={{ fontFamily: "inherit" }}
          />
        </label>
        <label className="toggle" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input type="checkbox" checked={compact} onChange={(e) => setCompact(e.target.checked)} />
          <span>Compact mode — denser header &amp; lists</span>
        </label>
        <label className="toggle" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input type="checkbox" checked={showDesc} onChange={(e) => setShowDesc(e.target.checked)} />
          <span>Show description in header</span>
        </label>
        <label className="toggle" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={showMeta} onChange={(e) => setShowMeta(e.target.checked)} />
          <span>Show meta row (priority, dates, visibility)</span>
        </label>
      </form>
    </Modal>
  );
}

// ===== Edit project (name / description / priority / status / icon / colour /
//        term / lead / dates) ===============================================

/** The project's own status codes (WorkDb.updateProject). value=code, Title-Case label. */
const PROJECT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "not_started", label: "Not started" },
  { value: "on_track", label: "On track" },
  { value: "at_risk", label: "At risk" },
  { value: "delayed", label: "Delayed" },
  { value: "on_hold", label: "On hold" },
  { value: "done", label: "Done" },
];

/** The project's own priority codes (WorkDb.updateProject). */
const PROJECT_PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

/** A unix-second timestamp → the yyyy-mm-dd in LOCAL time, for an <input type=date>. */
function dateInputValue(unixSec?: number | null): string {
  if (!unixSec) return "";
  const d = new Date(unixSec * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** A yyyy-mm-dd <input type=date> string → unix seconds at LOCAL start-of-day
 *  (or null when empty). Inverse of dateInputValue. */
function dateInputToUnix(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(new Date(y, m - 1, d).getTime() / 1000);
}

function EditProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Lead options reuse the same active-user roster the assignment pickers use.
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "projects", field: "assignee" }), []);
  const assignees = useMemo<Assignee[]>(() => assigneesApi.data?.assignees ?? [], [assigneesApi.data]);
  // Term options reuse the academic-terms config endpoint (same as the create/rollover flow).
  const termsApi = useApi((signal) => listTerms(signal), []);
  const terms = useMemo<AcademicTermRow[]>(() => termsApi.data?.terms ?? [], [termsApi.data]);

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [priority, setPriority] = useState(project.priority ?? "");
  const [status, setStatus] = useState(project.status ?? "");
  const [icon, setIcon] = useState(project.icon ?? "");
  const [color, setColor] = useState(project.color ?? "");
  const [term, setTerm] = useState(project.term ?? "");
  const [leadId, setLeadId] = useState(project.lead_id ?? "");
  const [startDate, setStartDate] = useState(dateInputValue(project.start_date));
  const [endDate, setEndDate] = useState(dateInputValue(project.end_date));
  const [teamsWebhook, setTeamsWebhook] = useState(project.teams_webhook_url ?? "");

  const { run, pending, error } = useMutation(updateProject);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    // Build a patch of ONLY changed fields. For nullable text fields an empty
    // input clears to null; selects send the new code (or null for "— None —").
    const patch: UpdateProjectInput = {};

    if (name.trim() !== project.name) patch.name = name.trim();

    const descNext = description.trim() ? description.trim() : null;
    if (descNext !== (project.description ?? null)) patch.description = descNext;

    if (priority && priority !== (project.priority ?? "")) patch.priority = priority;
    if (status && status !== (project.status ?? "")) patch.status = status;

    const iconNext = icon.trim() ? icon.trim() : null;
    if (iconNext !== (project.icon ?? null)) patch.icon = iconNext;

    const colorNext = color.trim() ? color.trim() : null;
    if (colorNext !== (project.color ?? null)) patch.color = colorNext;

    const termNext = term ? term : null;
    if (termNext !== (project.term ?? null)) patch.term = termNext;

    const leadNext = leadId ? leadId : null;
    if (leadNext !== (project.lead_id ?? null)) patch.lead_id = leadNext;

    const startNext = dateInputToUnix(startDate);
    if (startNext !== (project.start_date ?? null)) patch.start_date = startNext;

    const endNext = dateInputToUnix(endDate);
    if (endNext !== (project.end_date ?? null)) patch.end_date = endNext;

    const teamsNext = teamsWebhook.trim() ? teamsWebhook.trim() : null;
    if (teamsNext !== (project.teams_webhook_url ?? null)) patch.teams_webhook_url = teamsNext;

    // Nothing changed — just close.
    if (Object.keys(patch).length === 0) {
      onSaved();
      return;
    }

    const res = await run(project.id, patch);
    if (res) onSaved();
  }

  return (
    <Modal
      title={`Edit "${project.name}"`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="edit-project-form"
            disabled={pending || !name.trim()}
          >
            {pending ? <Spinner label="Saving…" /> : "Save changes"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="edit-project-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Project name"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="label">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What is this project about?"
          />
        </label>

        <label className="field">
          <span className="label">Microsoft Teams channel</span>
          <input
            type="url"
            value={teamsWebhook}
            onChange={(e) => setTeamsWebhook(e.target.value)}
            placeholder="https://… Incoming Webhook URL"
          />
          <span className="hint">
            Paste the channel's Incoming Webhook URL (Teams → channel → Connectors → Incoming Webhook).
            Commenters can then opt to mirror a comment into that channel.
          </span>
        </label>

        <div className="row">
          <label className="field">
            <span className="label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="">—</option>
              {PROJECT_PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">—</option>
              {PROJECT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Icon (emoji)</span>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="🎓"
              maxLength={4}
            />
          </label>
          <label className="field">
            <span className="label">Colour</span>
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <input
                type="color"
                aria-label="Project colour"
                value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : "#5b8def"}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: 44, padding: 2 }}
              />
              <input
                type="text"
                aria-label="Project colour hex"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#5b8def"
              />
            </div>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Term</span>
            <select value={term} onChange={(e) => setTerm(e.target.value)} disabled={termsApi.loading}>
              <option value="">— None —</option>
              {terms.map((t) => (
                <option key={t.id} value={t.code}>
                  {t.name}
                  {t.is_current ? " (current)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Lead</span>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} disabled={assigneesApi.loading}>
              <option value="">— None —</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Start date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">End date</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
      </form>
    </Modal>
  );
}

/** Term rollover: clone this project (boards, views, open tasks) into a new
 *  academic term. Shift days defaults to the gap between the two terms' start
 *  dates and stays overridable. */
function RolloverModal({
  project,
  onClose,
  onDone,
}: {
  project: ProjectRow;
  onClose: () => void;
  onDone: (newProjectId: string) => void;
}) {
  const termsApi = useApi((signal) => listTerms(signal), []);
  const terms = useMemo<AcademicTermRow[]>(() => termsApi.data?.terms ?? [], [termsApi.data]);
  const rolloverMut = useMutation(rolloverProject);

  const [key, setKey] = useState(`${project.key}-NEXT`);
  const [name, setName] = useState(project.name);
  const [termCode, setTermCode] = useState("");
  // Empty = use the computed default; any number = explicit override.
  const [shiftDays, setShiftDays] = useState("");

  // Computed default shift: target term start − source term start, in days
  // (only when both terms have start dates). Mirrors the backend default.
  const computedShift = useMemo(() => {
    if (!project.term || !termCode) return 0;
    const src = terms.find((t) => t.code === project.term);
    const dst = terms.find((t) => t.code === termCode);
    if (src?.starts_on == null || dst?.starts_on == null) return 0;
    return Math.round((dst.starts_on - src.starts_on) / 86400);
  }, [project.term, termCode, terms]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!key.trim()) return;
    const override = shiftDays.trim() === "" ? undefined : Number(shiftDays);
    if (override !== undefined && !Number.isFinite(override)) return;
    const res = await rolloverMut.run(project.id, {
      key: key.trim(),
      ...(name.trim() && name.trim() !== project.name ? { name: name.trim() } : {}),
      ...(termCode ? { term: termCode } : {}),
      ...(override !== undefined ? { shift_days: override } : {}),
    });
    if (res) onDone(res.project.id);
  }

  return (
    <Modal title={`Roll over "${project.name}"`} onClose={onClose}>
      {rolloverMut.error ? <Alert kind="error">{rolloverMut.error}</Alert> : null}
      <form onSubmit={onSubmit}>
        <p className="sub" style={{ marginTop: 0 }}>
          Copies this project — boards, views, and all unfinished tasks — into a new
          project for the next term. Completed tasks are left behind; task dates are
          shifted into the new term.
        </p>
        <label className="field">
          <span className="label">New project key</span>
          <input type="text" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ORIEN-F27" required />
        </label>
        <label className="field">
          <span className="label">New project name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Target term</span>
          <select value={termCode} onChange={(e) => setTermCode(e.target.value)}>
            <option value="">No term</option>
            {terms.map((t) => (
              <option key={t.id} value={t.code}>
                {t.name}
                {t.is_current ? " (current)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">
            Shift task dates by (days){" "}
            <span className="faint">default: {computedShift}</span>
          </span>
          <input
            type="number"
            value={shiftDays}
            onChange={(e) => setShiftDays(e.target.value)}
            placeholder={String(computedShift)}
          />
        </label>
        <div className="btn-row">
          <button className="btn primary" type="submit" disabled={rolloverMut.pending || !key.trim()}>
            {rolloverMut.pending ? <Spinner label="Rolling over…" /> : "Roll over"}
          </button>
          <button className="btn" type="button" onClick={onClose} disabled={rolloverMut.pending}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Dispatch a saved view to the renderer for its type. All types read the same
 *  task data; the view only changes the shape/grouping. */
function ViewRenderer({
  projectId,
  view,
  boards,
}: {
  projectId: string;
  view: SavedViewRow;
  boards: BoardSummary[];
}) {
  switch (view.view_type as ViewType) {
    case "board":
      return <BoardViewPanel view={view} boards={boards} />;
    case "table":
      return <TableTab projectId={projectId} />;
    case "timeline":
      return <TimelineTab projectId={projectId} />;
    case "calendar":
      return <CalendarTab projectId={projectId} />;
    case "workload":
      return <WorkloadTab projectId={projectId} />;
    case "chart":
      return <ChartTab projectId={projectId} />;
    case "list":
    default:
      return <TasksTab projectId={projectId} />;
  }
}

/** Board view → resolve the project's board and open the live Kanban page. The
 *  board view's config may name a board_id; otherwise use the project's board. */
function BoardViewPanel({ view, boards }: { view: SavedViewRow; boards: BoardSummary[] }) {
  let configBoardId: string | undefined;
  try {
    configBoardId = view.config_json
      ? (JSON.parse(view.config_json) as { board_id?: string }).board_id
      : undefined;
  } catch {
    configBoardId = undefined;
  }
  const [pickedId, setPickedId] = useState<string | undefined>(undefined);
  const boardId = pickedId ?? configBoardId ?? boards[0]?.id;

  if (!boardId) {
    return (
      <div className="card">
        <div className="card-body">
          <EmptyState>This project has no board yet.</EmptyState>
        </div>
      </div>
    );
  }

  // Render the live Kanban inline (columns + cards + drag + presence) — no
  // navigation to a separate screen. A switcher appears only with >1 board.
  return (
    <div>
      {boards.length > 1 ? (
        <div className="btn-row" style={{ marginBottom: 12 }}>
          {boards.map((b) => (
            <button
              key={b.id}
              className={`btn small${b.id === boardId ? " primary" : ""}`}
              onClick={() => setPickedId(b.id)}
            >
              {b.name}
            </button>
          ))}
        </div>
      ) : null}
      <BoardCanvas boardId={boardId} />
    </div>
  );
}

const TASKS_SORT: Record<string, SortAccessor<TaskRow>> = {
  title: (t) => t.title,
  status: (t) => t.status_code ?? t.status,
  priority: (t) => t.priority_code ?? t.priority_id,
  assignee: (t) => t.assignee_name ?? t.assignee_id,
  estimate: (t) => t.estimate_minutes,
  points: (t) => t.story_points,
};

function TasksTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listProjectTasks(projectId, {}, signal),
    [projectId],
  );
  const priorities = usePriorities();
  const deleteMut = useMutation(deleteTask);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const tasks = data?.tasks ?? [];
  const { rows: sortedTasks, sort, toggle } = useTableSort(tasks, TASKS_SORT);
  return (
    <div className="card">
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading tasks…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : tasks.length === 0 ? (
          <EmptyState>No tasks yet. Create the first one.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh field="title" sort={sort} onSort={toggle}>Title</SortTh>
                <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
                <SortTh field="assignee" sort={sort} onSort={toggle}>Assignee</SortTh>
                <SortTh field="estimate" sort={sort} onSort={toggle}>Estimate</SortTh>
                <SortTh field="points" sort={sort} onSort={toggle}>Points</SortTh>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((t) => {
                const prCode = taskPriorityCode(t, priorities);
                return (
                <tr key={t.id}>
                  <td>
                    <button
                      type="button"
                      className="task-title-link"
                      onClick={() => setOpenTaskId(t.id)}
                      title="Open task"
                    >
                      <strong>{t.title}</strong>
                    </button>
                    {t.origin_ticket_id ? <span className="pill" style={{ marginLeft: 6 }}>from ticket</span> : null}
                  </td>
                  <td>{(t.status_code ?? t.status) ? <StatusBadge code={(t.status_code ?? t.status)!} /> : "—"}</td>
                  <td>{prCode ? <PriorityBadge code={prCode} /> : "—"}</td>
                  <td>
                    {t.assignee_name || (t.assignee_id ? <span className="mono faint">{t.assignee_id.slice(0, 8)}…</span> : <span className="faint">—</span>)}
                  </td>
                  <td className="num">{formatEstimate(t.estimate_minutes)}</td>
                  <td className="num">{t.story_points ?? "—"}</td>
                  <td className="row-actions">
                    <button
                      className="btn small danger"
                      disabled={deleteMut.pending}
                      onClick={() =>
                        void confirmDialog({ message: `Delete project task "${t.title}"?`, danger: true }).then((yes) => {
                          if (yes) void deleteMut.run(t.id).then((ok) => ok && reload());
                        })
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {openTaskId ? (
        <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={reload} />
      ) : null}
    </div>
  );
}

/** Count items per group key, sorted by count descending. */
function chartGroupCounts<T>(items: T[], keyOf: (t: T) => string): { label: string; value: number }[] {
  const m = new Map<string, number>();
  for (const it of items) {
    const k = keyOf(it) || "—";
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/** One horizontal-bar chart card (label · bar · count). */
function ChartCard({ title, rows }: { title: string; rows: { label: string; value: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((n, r) => n + r.value, 0);
  return (
    <div className="card">
      <div className="card-head">
        <h3>{title}</h3>
        <span className="faint">{total}</span>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <EmptyState>No data.</EmptyState>
        ) : (
          <div className="bars">
            {rows.map((r) => (
              <div className="bar-row" key={r.label} title={`${r.label}: ${r.value}`}>
                <div className="bar-label">{r.label}</div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
                </div>
                <div className="bar-value num">{r.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Chart view — in-project breakdowns of this project's tasks (by status,
 *  priority, and assignee). Independent of Dashboards (which build cross-project
 *  widgets); this reads the same task data the other views use. */
function ChartTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listProjectTasks(projectId, {}, signal),
    [projectId],
  );
  const priorities = usePriorities();
  const tasks = data?.tasks ?? [];

  if (loading)
    return (
      <div className="card">
        <div className="card-body">
          <Loading label="Loading chart…" />
        </div>
      </div>
    );
  if (error)
    return (
      <div className="card">
        <div className="card-body">
          <ErrorState error={error} onRetry={reload} />
        </div>
      </div>
    );
  if (tasks.length === 0)
    return (
      <div className="card">
        <div className="card-body">
          <EmptyState>No tasks to chart yet — create some tasks to see the breakdown.</EmptyState>
        </div>
      </div>
    );

  const byStatus = chartGroupCounts(tasks, (t) => t.status_code ?? t.status ?? "—");
  const byPriority = chartGroupCounts(tasks, (t) => taskPriorityCode(t, priorities) ?? "—");
  const byAssignee = chartGroupCounts(tasks, (t) => t.assignee_name || "Unassigned");

  return (
    <div className="chart-grid">
      <ChartCard title="Tasks by status" rows={byStatus} />
      <ChartCard title="Tasks by priority" rows={byPriority} />
      <ChartCard title="Tasks by assignee" rows={byAssignee} />
    </div>
  );
}

function BacklogTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => getBacklog(projectId, signal),
    [projectId],
  );
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const backlog = data?.backlog ?? [];
  return (
    <div className="card">
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading backlog…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : backlog.length === 0 ? (
          <EmptyState>Backlog is empty.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {backlog.map((t, i) => (
                <tr
                  key={t.id}
                  className="clickable"
                  onClick={() => setOpenTaskId(t.id)}
                  title="Open task"
                >
                  <td className="num faint">{i + 1}</td>
                  <td>
                    <button
                      type="button"
                      className="task-title-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTaskId(t.id);
                      }}
                      title="Open task"
                    >
                      {t.title}
                    </button>
                  </td>
                  <td className="num">{t.story_points ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {openTaskId ? (
        <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={reload} />
      ) : null}
    </div>
  );
}

/** A sensible per-person capacity (open tasks) before a lane reads as "loaded".
 *  Faint threshold marker is drawn at this point; fill colour shifts past it. */
const WORKLOAD_CAPACITY = 8;

/** Load tone: green up to capacity, amber up to 1.5×, red beyond. */
function loadTone(load: number, capacity: number): "ok" | "warn" | "over" {
  if (load <= capacity) return "ok";
  if (load <= capacity * 1.5) return "warn";
  return "over";
}

function WorkloadTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => getWorkload(projectId, signal),
    [projectId],
  );
  // Sort heaviest-first; unassigned always last so it reads as a backlog bucket.
  const rows = useMemo(() => {
    const list = [...(data?.workload ?? [])];
    list.sort((a, b) => {
      if (!a.assignee_id) return 1;
      if (!b.assignee_id) return -1;
      return b.open_tasks - a.open_tasks;
    });
    return list;
  }, [data]);

  const hasPoints = rows.some((r) => (r.story_points ?? 0) > 0);
  // Scale: the larger of observed max or the capacity line so the marker sits sensibly.
  const max = Math.max(WORKLOAD_CAPACITY, ...rows.map((r) => r.open_tasks));
  const capPct = (WORKLOAD_CAPACITY / max) * 100;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Workload</h3>
        <span className="faint">capacity ~{WORKLOAD_CAPACITY} open / person</span>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading workload…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState>No open work to distribute.</EmptyState>
        ) : (
          <div className="workload">
            {rows.map((r) => {
              const tone = loadTone(r.open_tasks, WORKLOAD_CAPACITY);
              const name = r.assignee_name || (r.assignee_id ? r.assignee_id.slice(0, 8) : "Unassigned");
              const isUnassigned = !r.assignee_id;
              return (
                <div className="workload-row" key={r.assignee_id ?? "unassigned"}>
                  <div className="workload-who">
                    <Avatar name={isUnassigned ? null : name} size="sm" />
                    <span className="workload-name" title={name}>
                      {name}
                    </span>
                  </div>
                  <div className="workload-track">
                    <div
                      className={`workload-fill ${tone}`}
                      style={{ width: `${(r.open_tasks / max) * 100}%` }}
                    />
                    <span
                      className="workload-cap"
                      style={{ left: `${capPct}%` }}
                      title={`Capacity ~${WORKLOAD_CAPACITY}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="workload-meta">
                    <span className={`workload-count ${tone}`}>{r.open_tasks}</span>
                    <span className="workload-unit">open</span>
                    {hasPoints && (r.story_points ?? 0) > 0 ? (
                      <span className="workload-pts">{r.story_points} pt</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function isForbiddenError(error: string): boolean {
  return error.toLowerCase().startsWith("forbidden");
}

/** Shown when project load returns 403: lets the user request access. */
function RequestAccessPanel({ projectId }: { projectId: string }) {
  const { run, pending, error } = useMutation(requestProjectAccess);
  const [role, setRole] = useState<ProjectRole>("editor");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const res = await run(projectId, { requested_role: role, ...(note.trim() ? { note: note.trim() } : {}) });
    if (res) setSent(true);
  }

  return (
    <div className="card" style={{ maxWidth: 540 }}>
      <div className="card-head">
        <h3>Access required</h3>
      </div>
      <div className="card-body">
        {sent ? (
          <Alert kind="success">Request sent. A project manager or a tenant admin will review it.</Alert>
        ) : (
          <>
            <p className="sub">
              This is a private project and you're not a member yet. Request access below — a project
              manager or admin can approve it.
            </p>
            {error ? <Alert kind="error">{error}</Alert> : null}
            <form onSubmit={submit}>
              <label className="field">
                <span className="label">Requested role</span>
                <select value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>
                  <option value="viewer">Viewer (read only)</option>
                  <option value="editor">Editor (create / edit work)</option>
                  <option value="manager">Manager (full control)</option>
                </select>
              </label>
              <label className="field">
                <span className="label">Note (optional)</span>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why you need access" />
              </label>
              <button className="btn primary" type="submit" disabled={pending}>
                {pending ? <Spinner label="Sending…" /> : "Request access"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const MEMBERS_SORT: Record<string, SortAccessor<ProjectMember>> = {
  name: (m) => m.name,
  role: (m) => ROLE_LABEL[m.role],
};

/** Project membership + visibility management (managers and tenant admins). */
function MembersTab({ projectId, visibility }: { projectId: string; visibility: ProjectVisibility }) {
  const me = getCurrentUser();
  const meApi = useApi((signal) => getMe(undefined, signal), []);
  const membersApi = useApi((signal) => listProjectMembers(projectId, signal), [projectId]);
  const members = membersApi.data?.members ?? [];
  const { rows: sortedMembers, sort, toggle } = useTableSort(members, MEMBERS_SORT);

  const isAdmin = (meApi.data?.roles ?? []).includes("admin");
  const myRole = members.find((m) => m.user_id === me?.id)?.role;
  const canManage = isAdmin || myRole === "manager";

  const [vis, setVis] = useState<ProjectVisibility>(visibility);
  const addMut = useMutation(addProjectMember);
  const roleMut = useMutation(updateProjectMember);
  const removeMut = useMutation(removeProjectMember);
  const visMut = useMutation(setProjectVisibility);

  const [newUserId, setNewUserId] = useState("");
  const [newRole, setNewRole] = useState<ProjectRole>("editor");

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!newUserId.trim()) return;
    const res = await addMut.run(projectId, { user_id: newUserId.trim(), role: newRole });
    if (res) {
      setNewUserId("");
      membersApi.reload();
    }
  }

  async function changeRole(userId: string, role: ProjectRole) {
    if (await roleMut.run(projectId, userId, role)) membersApi.reload();
  }
  async function remove(userId: string) {
    if (await removeMut.run(projectId, userId)) membersApi.reload();
  }
  async function toggleVisibility(next: ProjectVisibility) {
    const res = await visMut.run(projectId, next);
    if (res) setVis(next);
  }

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Members</h3>
          <span className="faint">{members.length}</span>
        </div>
        <div className="card-body tight">
          {membersApi.loading ? (
            <Loading label="Loading members…" />
          ) : membersApi.error ? (
            <ErrorState error={membersApi.error} onRetry={membersApi.reload} />
          ) : members.length === 0 ? (
            <EmptyState>No explicit members yet.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                  <SortTh field="role" sort={sort} onSort={toggle}>Role</SortTh>
                  {canManage ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((m) => (
                  <tr key={m.user_id}>
                    <td>
                      <strong>{m.name}</strong>
                      <div className="mono faint" style={{ fontSize: "0.75rem" }}>{m.email}</div>
                    </td>
                    <td>
                      {canManage ? (
                        <select
                          value={m.role}
                          onChange={(e) => changeRole(m.user_id, e.target.value as ProjectRole)}
                          disabled={roleMut.pending}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="manager">Manager</option>
                        </select>
                      ) : (
                        <span className="pill">{ROLE_LABEL[m.role]}</span>
                      )}
                    </td>
                    {canManage ? (
                      <td>
                        <button
                          className="btn small"
                          onClick={() => remove(m.user_id)}
                          disabled={removeMut.pending}
                        >
                          Remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Access &amp; visibility</h3>
        </div>
        <div className="card-body">
          <div className="field">
            <span className="label">Visibility</span>
            <p className="hint" style={{ marginTop: 0 }}>
              {vis === "private"
                ? "Private — only members and tenant admins can see this project."
                : "Shared — every agent in your organization can see and work this project."}
            </p>
            {canManage ? (
              <div className="btn-row">
                <button
                  className={`btn small${vis === "shared" ? " primary" : ""}`}
                  onClick={() => toggleVisibility("shared")}
                  disabled={visMut.pending || vis === "shared"}
                >
                  Shared
                </button>
                <button
                  className={`btn small${vis === "private" ? " primary" : ""}`}
                  onClick={() => toggleVisibility("private")}
                  disabled={visMut.pending || vis === "private"}
                >
                  Private
                </button>
              </div>
            ) : null}
            {visMut.error ? <Alert kind="error">{visMut.error}</Alert> : null}
          </div>

          {canManage ? (
            <>
              <hr className="sep" />
              <h4 style={{ margin: "0 0 8px" }}>Add member</h4>
              {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}
              <form onSubmit={add}>
                <label className="field">
                  <span className="label">User ID</span>
                  <input
                    type="text"
                    value={newUserId}
                    onChange={(e) => setNewUserId(e.target.value)}
                    placeholder="usr_… (from Directory / Users)"
                  />
                </label>
                <label className="field">
                  <span className="label">Role</span>
                  <select value={newRole} onChange={(e) => setNewRole(e.target.value as ProjectRole)}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="manager">Manager</option>
                  </select>
                </label>
                <button className="btn primary" type="submit" disabled={addMut.pending || !newUserId.trim()}>
                  {addMut.pending ? <Spinner label="Adding…" /> : "Add member"}
                </button>
              </form>
            </>
          ) : (
            <p className="hint">Only project managers and tenant admins can change membership.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateTaskModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const priorities = usePriorities();
  const [title, setTitle] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [estimate, setEstimate] = useState("");
  const [department, setDepartment] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const { run, pending, error } = useMutation(createTask);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const est = estimate.trim();
    const res = await run({
      project_id: projectId,
      title: title.trim(),
      ...(priorityId ? { priority_id: priorityId } : {}),
      ...(est !== "" ? { estimate_minutes: Math.max(0, Math.round(Number(est))) } : {}),
      ...(department.trim() ? { department: department.trim() } : {}),
      ...(tags.length ? { tags } : {}),
    });
    if (res) onCreated();
  }

  return (
    <Modal
      title="New project task"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-task-form"
            disabled={pending || !title.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create task"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-task-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs doing?"
            autoFocus
            required
          />
        </label>

        <div className="row">
          <label className="field">
            <span className="label">Priority</span>
            <select
              value={priorityId}
              onChange={(e) => setPriorityId(e.target.value)}
              disabled={priorities.loading}
            >
              <option value="">—</option>
              {priorities.list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Estimate (min)</span>
            <input
              type="number"
              min={0}
              step={5}
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="e.g. 150"
            />
          </label>
        </div>

        <label className="field">
          <span className="label">Department</span>
          <input
            type="text"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="Owning office / department"
          />
        </label>

        <div className="field">
          <span className="label">Tags</span>
          <TagsEditor value={tags} onChange={setTags} inputId="create-task-tags" />
        </div>

        <p className="hint">
          Adds the task to the project backlog. Add it to a board column from the Board view.
        </p>
      </form>
    </Modal>
  );
}

// ===== Table view (tasks + custom-field columns) ===========================

type TableRow = TaskRow & { fields: Record<string, unknown> };

const TABLE_SORT: Record<string, SortAccessor<TableRow>> = {
  title: (t) => t.title,
  status: (t) => t.status_code ?? t.status,
  priority: (t) => t.priority_code ?? t.priority_id,
  assignee: (t) => t.assignee_name ?? t.assignee_id,
  points: (t) => t.story_points,
  estimate: (t) => t.estimate_minutes,
  due: (t) => t.due_date,
  department: (t) => t.department,
};

function TableTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => getProjectTable(projectId, signal),
    [projectId],
  );
  const priorities = usePriorities();
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "projects", field: "assignee" }), []);
  const assignees = useMemo<Assignee[]>(() => assigneesApi.data?.assignees ?? [], [assigneesApi.data]);

  const fields = data?.fields ?? [];
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  // Local copy so optimistic inline edits reflect immediately; reset when data reloads.
  const [rows, setRows] = useState<TableRow[]>([]);
  useEffect(() => {
    setRows(data?.rows ?? []);
  }, [data]);
  const { rows: sortedRows, sort, toggle } = useTableSort(rows, TABLE_SORT);

  // The inline editors are only offered for fields the task update API cleanly
  // supports (status_code, priority_id, assignee_id, story_points). Everything
  // else (custom fields) stays read-only but type-formatted.
  const saveMut = useMutation(updateTask);
  function patchRow(id: string, patch: Partial<TableRow>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  return (
    <div className="card">
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading table…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState>No tasks yet.</EmptyState>
        ) : (
          <div className="vtable-wrap">
            <table className="vtable">
              <thead>
                <tr>
                  <SortTh field="title" sort={sort} onSort={toggle} className="vt-sticky-col">Title</SortTh>
                  <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                  <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
                  <SortTh field="assignee" sort={sort} onSort={toggle}>Assignee</SortTh>
                  <SortTh field="points" sort={sort} onSort={toggle} className="vt-r">Points</SortTh>
                  <SortTh field="estimate" sort={sort} onSort={toggle} className="vt-r">Estimate</SortTh>
                  <SortTh field="due" sort={sort} onSort={toggle}>Due</SortTh>
                  <SortTh field="department" sort={sort} onSort={toggle}>Department</SortTh>
                  {fields.map((f) => (
                    <th key={f.id}>{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((t) => {
                  const prCode = taskPriorityCode(t, priorities);
                  return (
                    <tr key={t.id}>
                      <td className="vt-sticky-col vt-title" title={t.title}>
                        <button
                          type="button"
                          className="task-title-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenTaskId(t.id);
                          }}
                          title="Open task"
                        >
                          <strong>{t.title}</strong>
                        </button>
                        {t.origin_ticket_id ? <span className="pill vt-from">ticket</span> : null}
                      </td>

                      {/* Inline status — tasks use the task lifecycle vocabulary
                          (todo/in_progress/done), the SAME set as the task drawer,
                          list, and the project's done-rollups. Keep any pre-existing
                          non-standard value selectable so it isn't silently dropped. */}
                      <td>
                        {(() => {
                          const cur = t.status_code ?? t.status ?? "";
                          const opts = TASK_STATUSES.map((s) => ({ value: s.code, label: s.label }));
                          if (cur && !opts.some((o) => o.value === cur)) {
                            opts.push({ value: cur, label: cur });
                          }
                          return (
                        <InlineSelect
                          ariaLabel={`Status for ${t.title}`}
                          value={cur}
                          options={opts}
                          placeholder="—"
                          disabled={saveMut.pending}
                          render={(v) => (v ? <StatusBadge code={v} /> : <span className="faint">—</span>)}
                          onChange={async (v) => {
                            const prev = t.status_code;
                            patchRow(t.id, { status_code: v || null });
                            const ok = await saveMut.run(t.id, { status_code: v });
                            if (!ok) patchRow(t.id, { status_code: prev ?? null });
                          }}
                        />
                          );
                        })()}
                      </td>

                      {/* Inline priority (priority_id) */}
                      <td>
                        <InlineSelect
                          ariaLabel={`Priority for ${t.title}`}
                          value={t.priority_id ?? ""}
                          options={priorities.list.map((p) => ({ value: p.id, label: p.name }))}
                          placeholder="—"
                          disabled={saveMut.pending || priorities.loading}
                          render={() =>
                            prCode ? <PriorityBadge code={prCode} /> : <span className="faint">—</span>
                          }
                          onChange={async (v) => {
                            const prev = t.priority_id;
                            patchRow(t.id, { priority_id: v || null, priority_code: null });
                            const ok = await saveMut.run(t.id, { priority_id: v || null });
                            if (!ok) patchRow(t.id, { priority_id: prev ?? null });
                          }}
                        />
                      </td>

                      {/* Inline assignee (assignee_id) */}
                      <td>
                        <InlineSelect
                          ariaLabel={`Assignee for ${t.title}`}
                          value={t.assignee_id ?? ""}
                          options={assignees.map((a) => ({ value: a.id, label: a.name }))}
                          placeholder="Unassigned"
                          disabled={saveMut.pending || assigneesApi.loading}
                          render={(v) =>
                            v ? (
                              <span className="vt-assignee">
                                <Avatar name={t.assignee_name ?? labelOf(assignees, v)} size="sm" />
                                <span className="vt-assignee-name">
                                  {t.assignee_name ?? labelOf(assignees, v)}
                                </span>
                              </span>
                            ) : (
                              <span className="vt-assignee">
                                <Avatar name={null} size="sm" />
                                <span className="faint">Unassigned</span>
                              </span>
                            )
                          }
                          onChange={async (v) => {
                            const prevId = t.assignee_id;
                            const prevName = t.assignee_name;
                            patchRow(t.id, {
                              assignee_id: v || null,
                              assignee_name: v ? labelOf(assignees, v) : null,
                            });
                            const ok = await saveMut.run(t.id, { assignee_id: v || null });
                            if (!ok) patchRow(t.id, { assignee_id: prevId ?? null, assignee_name: prevName ?? null });
                          }}
                        />
                      </td>

                      {/* Inline story points (number) */}
                      <td className="vt-r">
                        <InlineNumber
                          ariaLabel={`Story points for ${t.title}`}
                          value={t.story_points ?? null}
                          disabled={saveMut.pending}
                          onChange={async (n) => {
                            const prev = t.story_points;
                            patchRow(t.id, { story_points: n });
                            const ok = await saveMut.run(t.id, { story_points: n });
                            if (!ok) patchRow(t.id, { story_points: prev ?? null });
                          }}
                        />
                      </td>

                      {/* Read-only built-ins */}
                      <td className="vt-r vt-num">{formatEstimate(t.estimate_minutes)}</td>
                      <td className="vt-num">{fmtDay(t.due_date)}</td>
                      <td>{t.department || <span className="faint">—</span>}</td>

                      {/* Custom fields (read-only, type-formatted) */}
                      {fields.map((f) => (
                        <td key={f.id} className="faint">
                          {formatFieldValue(t.fields?.[f.key])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {openTaskId ? (
        <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={reload} />
      ) : null}
    </div>
  );
}

function labelOf(opts: { id: string; name: string }[], id: string): string {
  return opts.find((o) => o.id === id)?.name ?? id.slice(0, 8);
}

/** A cell that shows a rendered value until clicked/focused, then swaps to a
 *  native <select> for the edit. Keyboard accessible (button → select). */
function InlineSelect({
  value,
  options,
  onChange,
  render,
  ariaLabel,
  placeholder,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  render: (value: string) => ReactNode;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <select
        className="vt-edit"
        autoFocus
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setEditing(false);
        }}
        onBlur={() => setEditing(false)}
      >
        <option value="">{placeholder ?? "—"}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <button
      type="button"
      className="vt-cell-btn"
      aria-label={`${ariaLabel} (click to edit)`}
      disabled={disabled}
      onClick={() => setEditing(true)}
    >
      {render(value)}
    </button>
  );
}

/** Inline numeric editor (story points). */
function InlineNumber({
  value,
  onChange,
  ariaLabel,
  disabled,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  if (editing) {
    return (
      <input
        className="vt-edit vt-edit-num"
        type="number"
        min={0}
        step={1}
        autoFocus
        aria-label={ariaLabel}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const v = draft.trim();
          onChange(v === "" ? null : Math.max(0, Math.round(Number(v))));
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }
  return (
    <button
      type="button"
      className="vt-cell-btn vt-cell-btn-num"
      aria-label={`${ariaLabel} (click to edit)`}
      disabled={disabled}
      onClick={() => {
        setDraft(value == null ? "" : String(value));
        setEditing(true);
      }}
    >
      {value ?? <span className="faint">—</span>}
    </button>
  );
}

function formatFieldValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ===== Timeline view (Gantt-style bars by start/due date) ==================

/** Px per day in the Gantt grid — wide enough that day labels and short bars
 *  stay readable (the grid scrolls horizontally for long ranges). */
const GANTT_DAY_W = 40;
const GANTT_NAME_W = 260;

function TimelineTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => getProjectTimeline(projectId, signal),
    [projectId],
  );
  const priorities = usePriorities();
  const tasks = useMemo(() => data?.tasks ?? [], [data]);
  const milestones = useMemo(() => data?.milestones ?? [], [data]);
  const dependencies = useMemo(() => data?.dependencies ?? [], [data]);
  const [colorBy, setColorBy] = useState<"status" | "priority">("status");
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  // Scheduled vs. unscheduled tasks.
  const scheduled = useMemo(() => tasks.filter((t) => t.start_date || t.due_date), [tasks]);
  const unscheduled = useMemo(() => tasks.filter((t) => !t.start_date && !t.due_date), [tasks]);

  // Day-aligned date window across all dated tasks + milestones, padded a little.
  const model = useMemo(() => {
    const stamps: number[] = [];
    for (const t of scheduled) {
      const s = t.start_date ?? t.due_date!;
      const e = t.due_date ?? t.start_date!;
      stamps.push(s, e);
    }
    for (const m of milestones) if (m.due_date) stamps.push(m.due_date);
    if (stamps.length === 0) return null;
    const minDay = dayStartSec(Math.min(...stamps)) - DAY_SEC; // 1-day left pad
    const maxDay = dayStartSec(Math.max(...stamps)) + 2 * DAY_SEC; // right pad
    const dayCount = Math.max(1, Math.round((maxDay - minDay) / DAY_SEC));
    const xForDay = (sec: number) => ((dayStartSec(sec) - minDay) / DAY_SEC) * GANTT_DAY_W;
    return { minDay, dayCount, xForDay, gridW: dayCount * GANTT_DAY_W };
  }, [scheduled, milestones]);

  // Row index per task id → used to draw dependency connectors between bars.
  const rowIndex = useMemo(() => {
    const m = new Map<string, number>();
    scheduled.forEach((t, i) => m.set(t.id, i));
    return m;
  }, [scheduled]);

  const ROW_H = 42;
  const BAR_H = 24;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Timeline</h3>
        <div className="seg" role="group" aria-label="Colour bars by">
          <button
            type="button"
            className={`seg-btn${colorBy === "status" ? " active" : ""}`}
            aria-pressed={colorBy === "status"}
            onClick={() => setColorBy("status")}
          >
            Status
          </button>
          <button
            type="button"
            className={`seg-btn${colorBy === "priority" ? " active" : ""}`}
            aria-pressed={colorBy === "priority"}
            onClick={() => setColorBy("priority")}
          >
            Priority
          </button>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading timeline…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : tasks.length === 0 ? (
          <EmptyState>No tasks to schedule.</EmptyState>
        ) : !model ? (
          <UnscheduledLane tasks={unscheduled} note="No tasks have start or due dates yet." onOpen={setOpenTaskId} />
        ) : (
          <>
            <div className="gantt" role="table" aria-label="Project timeline">
              <GanttHeader model={model} nameW={GANTT_NAME_W} />
              <div className="gantt-body">
                {/* Sticky task-name column */}
                <div className="gantt-names" style={{ width: GANTT_NAME_W }}>
                  {scheduled.map((t) => (
                    <div className="gantt-name" key={t.id} style={{ height: ROW_H }} title={t.title}>
                      <button
                        type="button"
                        className="task-title-link"
                        onClick={() => setOpenTaskId(t.id)}
                        title="Open task"
                      >
                        {t.title}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Scrollable grid with bars, today line, milestones, dependencies */}
                <div className="gantt-scroll">
                  <div
                    className="gantt-grid"
                    style={{ width: model.gridW, height: scheduled.length * ROW_H }}
                  >
                    <GanttGridLines model={model} height={scheduled.length * ROW_H} />
                    <GanttTodayLine model={model} height={scheduled.length * ROW_H} />

                    {/* Dependency connectors (real data: dependencies[]). */}
                    <DependencyLayer
                      deps={dependencies}
                      rowIndex={rowIndex}
                      tasks={scheduled}
                      model={model}
                      rowH={ROW_H}
                      barH={BAR_H}
                    />

                    {/* Task bars */}
                    {scheduled.map((t, i) => {
                      const start = t.start_date ?? t.due_date!;
                      const end = t.due_date ?? t.start_date!;
                      const x = model.xForDay(start);
                      const endX = model.xForDay(end) + GANTT_DAY_W; // inclusive end day
                      const w = Math.max(GANTT_DAY_W * 0.7, endX - x);
                      const color =
                        colorBy === "priority"
                          ? priorityToken(taskPriorityCode(t, priorities))
                          : statusToken(t.status_code ?? t.status);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className="gantt-bar"
                          style={{
                            top: i * ROW_H + (ROW_H - BAR_H) / 2,
                            left: x,
                            width: w,
                            height: BAR_H,
                            ["--bar-c" as string]: color,
                          }}
                          title={`${t.title} · ${fmtDay(start)} → ${fmtDay(end)}`}
                          onClick={() => setOpenTaskId(t.id)}
                        >
                          <span className="gantt-bar-label">{t.title}</span>
                        </button>
                      );
                    })}

                    {/* Milestone diamonds on the axis (real data: milestones[]). */}
                    {milestones.map((m) =>
                      m.due_date ? (
                        <div
                          key={m.id}
                          className="gantt-milestone"
                          style={{ left: model.xForDay(m.due_date) + GANTT_DAY_W / 2 }}
                          title={`${m.name} · ${fmtDay(m.due_date)}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                            <rect
                              x="2"
                              y="2"
                              width="10"
                              height="10"
                              rx="2"
                              transform="rotate(45 7 7)"
                              fill="var(--brand-2)"
                              stroke="var(--surface)"
                              strokeWidth="1.5"
                            />
                          </svg>
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              </div>
            </div>

            {milestones.length > 0 ? (
              <div className="gantt-legend">
                <span className="gantt-legend-item">
                  <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
                    <rect x="2" y="2" width="10" height="10" rx="2" transform="rotate(45 7 7)" fill="var(--brand-2)" />
                  </svg>
                  Milestone
                </span>
                {milestones.map((m) => (
                  <span key={m.id} className="pill" title={fmtFullDay(m.due_date)}>
                    {m.name} · {fmtDay(m.due_date)}
                  </span>
                ))}
              </div>
            ) : null}

            {unscheduled.length > 0 ? (
              <UnscheduledLane
                tasks={unscheduled}
                note={`${unscheduled.length} task${unscheduled.length === 1 ? "" : "s"} without dates`}
                onOpen={setOpenTaskId}
              />
            ) : null}
          </>
        )}
      </div>
      {openTaskId ? (
        <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={reload} />
      ) : null}
    </div>
  );
}

interface GanttModel {
  minDay: number;
  dayCount: number;
  xForDay: (sec: number) => number;
  gridW: number;
}

/** Month/day axis header. Sticky name spacer + a day ruler with month labels. */
function GanttHeader({ model, nameW }: { model: GanttModel; nameW: number }) {
  const days = useMemo(() => {
    const out: { x: number; date: Date; isMonthStart: boolean; isWeekend: boolean }[] = [];
    for (let i = 0; i < model.dayCount; i++) {
      const date = new Date((model.minDay + i * DAY_SEC) * 1000);
      out.push({
        x: i * GANTT_DAY_W,
        date,
        isMonthStart: date.getDate() === 1 || i === 0,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
      });
    }
    return out;
  }, [model]);

  return (
    <div className="gantt-head">
      <div className="gantt-head-spacer" style={{ width: nameW }} />
      <div className="gantt-head-scroll" id="gantt-head-scroll">
        <div className="gantt-ruler" style={{ width: model.gridW }}>
          {days.map((d, i) => (
            <div
              key={i}
              className={`gantt-day${d.isWeekend ? " weekend" : ""}`}
              style={{ left: d.x, width: GANTT_DAY_W }}
            >
              {d.isMonthStart ? (
                <span className="gantt-month">
                  {d.date.toLocaleDateString(undefined, { month: "short" })}
                </span>
              ) : null}
              <span className="gantt-dom">{d.date.getDate()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Faint vertical gridlines (one per week) + weekend shading. */
function GanttGridLines({ model, height }: { model: GanttModel; height: number }) {
  const lines: number[] = [];
  for (let i = 0; i < model.dayCount; i++) {
    const date = new Date((model.minDay + i * DAY_SEC) * 1000);
    if (date.getDay() === 1) lines.push(i * GANTT_DAY_W); // Monday boundaries
  }
  return (
    <svg className="gantt-gridlines" width={model.gridW} height={height} aria-hidden="true">
      {lines.map((x, i) => (
        <line key={i} x1={x} y1={0} x2={x} y2={height} stroke="var(--border)" strokeWidth="1" />
      ))}
    </svg>
  );
}

/** The "TODAY" vertical marker — only drawn if today falls inside the window. */
function GanttTodayLine({ model, height }: { model: GanttModel; height: number }) {
  const nowSec = Math.floor(Date.now() / 1000);
  const today = dayStartSec(nowSec);
  if (today < model.minDay || today > model.minDay + model.dayCount * DAY_SEC) return null;
  const x = model.xForDay(nowSec) + GANTT_DAY_W / 2;
  return (
    <div className="gantt-today" style={{ left: x, height }}>
      <span className="gantt-today-tag">Today</span>
    </div>
  );
}

/** Inline-SVG connectors from each task to the task it depends on (finish→start). */
function DependencyLayer({
  deps,
  rowIndex,
  tasks,
  model,
  rowH,
  barH,
}: {
  deps: { id: string; task_id: string; depends_on_id: string }[];
  rowIndex: Map<string, number>;
  tasks: TaskRow[];
  model: GanttModel;
  rowH: number;
  barH: number;
}) {
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const edges = deps.filter((d) => rowIndex.has(d.task_id) && rowIndex.has(d.depends_on_id));
  if (edges.length === 0) return null;
  const height = tasks.length * rowH;
  const width = model.gridW;
  const midY = (idx: number) => idx * rowH + rowH / 2;
  const endX = (t: TaskRow) =>
    model.xForDay(t.due_date ?? t.start_date!) + GANTT_DAY_W;
  const startX = (t: TaskRow) => model.xForDay(t.start_date ?? t.due_date!);
  return (
    <svg className="gantt-deps" width={width} height={height} aria-hidden="true">
      <defs>
        <marker id="gantt-arrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="var(--text-faint)" />
        </marker>
      </defs>
      {edges.map((d) => {
        const from = byId.get(d.depends_on_id)!; // predecessor (finish)
        const to = byId.get(d.task_id)!; // successor (start)
        const x1 = endX(from);
        const y1 = midY(rowIndex.get(from.id)!);
        const x2 = Math.max(startX(to) - 4, x1);
        const y2 = midY(rowIndex.get(to.id)!) - barH / 2 + barH / 2;
        const mx = (x1 + x2) / 2;
        return (
          <path
            key={d.id}
            d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="var(--text-faint)"
            strokeWidth="1.5"
            markerEnd="url(#gantt-arrow)"
          />
        );
      })}
    </svg>
  );
}

/** Lane for tasks with no start/due date — keeps them visible instead of dropped. */
function UnscheduledLane({
  tasks,
  note,
  onOpen,
}: {
  tasks: TaskRow[];
  note: string;
  onOpen: (id: string) => void;
}) {
  if (tasks.length === 0) return null;
  return (
    <div className="gantt-unscheduled">
      <div className="gantt-unscheduled-head">
        <span className="label">Unscheduled</span>
        <span className="faint">{note}</span>
      </div>
      <div className="gantt-unscheduled-chips">
        {tasks.map((t) => (
          <button
            key={t.id}
            type="button"
            className="pill task-title-link"
            title="Open task"
            onClick={() => onOpen(t.id)}
          >
            {t.title}
          </button>
        ))}
      </div>
    </div>
  );
}

// ===== Calendar view (tasks bucketed by due day) ===========================

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_MAX_CHIPS = 3;

/** Local yyyy-mm-dd key (matches how the API buckets due days, which we read back). */
function isoDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function CalendarTab({ projectId }: { projectId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => getProjectCalendar(projectId, signal),
    [projectId],
  );
  const priorities = usePriorities();
  const days = useMemo(() => data?.days ?? {}, [data]);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  // Month the user is viewing. Default to the current month, or the first month
  // that actually has tasks if "today" is empty but data exists elsewhere.
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const todayKey = isoDayKey(new Date());

  // Build the 6×7 grid of day cells spanning the visible month.
  const cells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - first.getDay()); // back up to Sunday
    const out: { date: Date; key: string; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      out.push({ date, key: isoDayKey(date), inMonth: date.getMonth() === month });
    }
    return out;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const hasAny = Object.values(days).some((list) => (list?.length ?? 0) > 0);

  return (
    <div className="card">
      <div className="card-head cal-head">
        <h3>{monthLabel}</h3>
        <div className="cal-nav">
          <button
            type="button"
            className="btn small"
            aria-label="Previous month"
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              const n = new Date();
              setCursor(new Date(n.getFullYear(), n.getMonth(), 1));
            }}
          >
            Today
          </button>
          <button
            type="button"
            className="btn small"
            aria-label="Next month"
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
          >
            ›
          </button>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading calendar…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            {!hasAny ? (
              <p className="hint" style={{ marginTop: 0 }}>
                No tasks with due dates in the loaded window. Months still navigate.
              </p>
            ) : null}
            <div className="cal" role="grid" aria-label={`${monthLabel} calendar`}>
              <div className="cal-weekdays" role="row">
                {WEEKDAY_LABELS.map((w) => (
                  <div key={w} className="cal-weekday" role="columnheader">
                    {w}
                  </div>
                ))}
              </div>
              <div className="cal-grid">
                {cells.map((c) => {
                  const list = days[c.key] ?? [];
                  const shown = list.slice(0, CAL_MAX_CHIPS);
                  const extra = list.length - shown.length;
                  const isToday = c.key === todayKey;
                  return (
                    <div
                      key={c.key}
                      role="gridcell"
                      className={`cal-cell${c.inMonth ? "" : " muted"}${isToday ? " today" : ""}`}
                    >
                      <div className="cal-cell-num">
                        <span className={isToday ? "cal-today-dot" : ""}>{c.date.getDate()}</span>
                      </div>
                      <div className="cal-chips">
                        {shown.map((t: TaskRow) => {
                          const prCode = taskPriorityCode(t, priorities);
                          const color = prCode
                            ? priorityToken(prCode)
                            : statusToken(t.status_code ?? t.status);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              className="cal-chip"
                              style={{ ["--chip-c" as string]: color }}
                              title={`${t.title}${prCode ? ` · ${prCode}` : ""} — open task`}
                              onClick={() => setOpenTaskId(t.id)}
                            >
                              {t.title}
                            </button>
                          );
                        })}
                        {extra > 0 ? <span className="cal-more">+{extra} more</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      {openTaskId ? (
        <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={reload} />
      ) : null}
    </div>
  );
}

// ===== Create a new saved view =============================================

function CreateViewModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (view: SavedViewRow) => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<ViewType>("list");
  const { run, pending, error } = useMutation(createView);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await run(projectId, { name: name.trim(), view_type: type });
    if (res) onCreated(res.view);
  }

  return (
    <Modal
      title="Add a view"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-view-form"
            disabled={pending || !name.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create view"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-view-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">View type</span>
          <select value={type} onChange={(e) => setType(e.target.value as ViewType)}>
            {VIEW_TYPES.map((vt) => (
              <option key={vt} value={vt}>
                {VIEW_META[vt].icon} {VIEW_META[vt].label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={VIEW_META[type].label}
            autoFocus
            required
          />
        </label>
        <p className="hint">
          All views read the same tasks — the type only changes how they're shaped (grouped, scheduled,
          tabulated). You can switch between them from the tabs above.
        </p>
      </form>
    </Modal>
  );
}
